import type { QuantaHeaders } from './auth';
import { sha256Hex } from './auth';
import { quantaApi } from './quantaApi';

export interface LeaderboardWalletInput {
  origin: string;
  userId: string;
  revision: number;
  network: 'mainnet' | 'testnet';
  authorityAddress: string;
  authorityPublicKey: string;
  walletAddress: string;
  walletPublicKey: string;
}

const address = (value: string) => (/^(bc1|tb1|bcrt1)/i.test(value) ? value.toLowerCase() : value);
const key = (wallet: string, value: string) =>
  (/^(bc1p|tb1p|bcrt1p)/i.test(wallet) && value.length === 66 ? value.slice(2) : value).toLowerCase();

export function leaderboardWalletContext(input: LeaderboardWalletInput, nonce: string) {
  return {
    operation: 'ducat:quanta:leaderboard-wallet:v1',
    origin: input.origin,
    userId: input.userId,
    revision: input.revision,
    network: input.network,
    authorityAddress: address(input.authorityAddress),
    authorityPublicKey: key(input.authorityAddress, input.authorityPublicKey),
    walletAddress: address(input.walletAddress),
    walletPublicKey: key(input.walletAddress, input.walletPublicKey),
    nonce,
  };
}

export function leaderboardWalletMessage(input: LeaderboardWalletInput, nonce: string) {
  return `Ducat Quanta leaderboard address\nShow this wallet address on the leaderboard for the account below. This does not change login, ownership or points.\n${JSON.stringify(leaderboardWalletContext(input, nonce), null, 2)}`;
}

export async function proveLeaderboardWallet(
  input: LeaderboardWalletInput,
  headers: QuantaHeaders,
  sign: (address: string, message: string) => Promise<string>,
  assertCurrent: () => void,
) {
  assertCurrent();
  const { data: response } = await quantaApi.post('/leaderboard-wallet/challenge', input, { headers });
  const proof = response.data;
  assertCurrent();
  if (
    typeof proof?.nonce !== 'string' ||
    !/^[a-f0-9]{64}$/.test(proof.nonce) ||
    !Number.isFinite(Date.parse(proof.expiresAt)) ||
    Date.parse(proof.expiresAt) <= Date.now()
  ) {
    throw new Error('Invalid leaderboard wallet approval');
  }
  const message = leaderboardWalletMessage(input, proof.nonce);
  if (
    message.length > 800 ||
    proof.message !== message ||
    proof.hash !== (await sha256Hex(message)) ||
    JSON.stringify(proof.context) !== JSON.stringify(leaderboardWalletContext(input, proof.nonce))
  ) {
    throw new Error('Leaderboard wallet approval does not match this account');
  }
  assertCurrent();
  const signature = await sign(input.walletAddress, message);
  assertCurrent();
  if (!signature || Date.parse(proof.expiresAt) <= Date.now()) throw new Error('Leaderboard wallet approval expired or was cancelled');
  await quantaApi.post('/leaderboard-wallet/confirm', { ...input, nonce: proof.nonce, signature }, { headers });
  assertCurrent();
}
